import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { usePushEnrolment, type AlertsState } from '../../hooks/usePushEnrolment';
import { api } from '../../lib/api';
import { isAndroidDevice, isIosDevice } from '../../lib/platform';

/**
 * The one switch for alerts on this device, present in every state.
 *
 * Until this existed the only control was the enrolment banner, which shows only while
 * a device is NOT subscribed — so once somebody turned alerts on there was no way to
 * turn them off short of "forget all my devices", and no way to see the state at all.
 *
 * Four states behind two positions (see `AlertsState`), and two platform states, all of
 * which render. A switch that is disabled with its reason is a switch somebody can act
 * on — install the app, fix the OS setting; a missing control reads as a broken app.
 *
 * Nothing here is optimistic. The position is whatever `readPushEnrolment` says after the
 * call resolves, so a subscribe that failed leaves the switch off and says why.
 */
export function PushToggle({ onChanged }: { onChanged?: () => void }) {
  const { t } = useTranslation();
  const { state, result, busy, enable, disable } = usePushEnrolment();

  const checked = state === 'on';
  const actionable = state === 'never-asked' || state === 'on' || state === 'off';

  async function toggle() {
    if (checked) await disable();
    else await enable();
    onChanged?.();
  }

  return (
    <div className="border-t border-slate-800 px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p id="push-toggle-label" className="text-sm font-semibold text-slate-100">
            {t('settings.toggle.label')}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">{t(STATE_COPY[state])}</p>
        </div>
        <Switch
          checked={checked}
          disabled={busy || !actionable}
          busy={busy}
          onToggle={() => void toggle()}
        />
      </div>

      {state === 'blocked' && <BlockedInstructions />}

      {result?.status === 'no-vapid-key' && (
        <p className="mt-2 text-xs text-amber-400">{t('notifications.push.noKey')}</p>
      )}
      {result?.status === 'failed' && (
        <p role="alert" className="mt-2 text-xs text-red-400">
          {t('settings.toggle.failed', { error: result.error })}
        </p>
      )}

      <p className="mt-2 text-xs text-slate-500">{t('settings.toggle.perDevice')}</p>

      <TestNotification enabled={checked && !busy} />
    </div>
  );
}

const STATE_COPY: Record<AlertsState, string> = {
  loading: 'settings.toggle.state.loading',
  'never-asked': 'settings.toggle.state.neverAsked',
  on: 'settings.toggle.state.on',
  off: 'settings.toggle.state.off',
  blocked: 'settings.toggle.state.blocked',
  'install-first': 'settings.toggle.state.installFirst',
  unsupported: 'settings.toggle.state.unsupported',
};

/**
 * A real switch: `role="switch"` with `aria-checked`, and the whole 48px-tall box is the
 * hit area even though the drawn track is smaller — a gloved thumb aims at the control,
 * not at the artwork.
 */
function Switch({
  checked,
  disabled,
  busy,
  onToggle,
}: {
  checked: boolean;
  disabled: boolean;
  busy: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby="push-toggle-label"
      aria-busy={busy || undefined}
      disabled={disabled}
      onClick={onToggle}
      className="flex h-12 min-h-[48px] w-[72px] shrink-0 items-center justify-center bg-transparent p-0 disabled:opacity-40"
    >
      <span
        aria-hidden="true"
        className={`relative block h-8 w-14 rounded-full transition-colors ${
          checked ? 'bg-brookfields-green ring-1 ring-brookfields-gold/70' : 'bg-slate-700'
        }`}
      >
        <span
          className={`absolute top-1 size-6 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-7' : 'translate-x-1'
          }`}
        />
      </span>
    </button>
  );
}

/**
 * The state that traps people. Whoever tapped Block on the first-run prompt is
 * unreachable by this app until they change it in the OS, and the app cannot ask
 * twice — so the only useful thing to show is the exact path, for this platform.
 */
function BlockedInstructions() {
  const { t } = useTranslation();
  const path = isIosDevice()
    ? 'settings.toggle.blocked.ios'
    : isAndroidDevice()
      ? 'settings.toggle.blocked.android'
      : 'settings.toggle.blocked.other';

  return (
    <p className="mt-2 flex items-start gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
      <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>{t(path)}</span>
    </p>
  );
}

type TestOutcome =
  | { kind: 'sent'; count: number }
  | { kind: 'none' }
  | { kind: 'failed'; error: string };

/**
 * "Send me a test notification" is what makes the switch believable. Staff will insist
 * they never received an alert, and this settles it in five seconds without anyone
 * reading a log — which matters, because push failures are counted and never logged
 * individually. Sends only to the calling account's own devices.
 */
function TestNotification({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<TestOutcome | null>(null);

  async function send() {
    setSending(true);
    setOutcome(null);
    try {
      const { sent } = await api.sendTestPush();
      setOutcome(sent > 0 ? { kind: 'sent', count: sent } : { kind: 'none' });
    } catch (error) {
      setOutcome({ kind: 'failed', error: error instanceof Error ? error.message : String(error) });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => void send()}
        disabled={!enabled || sending}
        className="min-h-[48px] w-full rounded-lg border border-slate-700 bg-transparent px-4 text-sm font-medium text-slate-200 disabled:opacity-50"
      >
        {sending ? t('settings.toggle.test.sending') : t('settings.toggle.test.button')}
      </button>
      {outcome && (
        <p
          role={outcome.kind === 'failed' ? 'alert' : 'status'}
          className={`mt-2 text-xs ${outcome.kind === 'sent' ? 'text-emerald-400' : outcome.kind === 'none' ? 'text-amber-400' : 'text-red-400'}`}
        >
          {outcome.kind === 'sent' && t('settings.toggle.test.sent', { count: outcome.count })}
          {outcome.kind === 'none' && t('settings.toggle.test.none')}
          {outcome.kind === 'failed' && t('settings.toggle.test.failed', { error: outcome.error })}
        </p>
      )}
    </div>
  );
}
